// =============================================================================
// SŁOWNIK WSPÓLNY RECEPTORA — panel Universe i okno agenta (plan okna agenta
// 0.6.0, D8, §11.7)
//
// Jedno miejsce dla słów, którymi Universe mówi o pracy agenta Receptora:
// etykiet rodzajów zadań, zdań stanów kursora obiegu dokumentów i powodów
// harmonogramu, opisów stanu produkcji i powodów „nie sprawdzono” zdolności
// oraz typów odpowiedzi `przeplyw-agenta/1`. Czytają go DWA procesy: powłoka
// (`rodzajeZadanReceptora.ts`, `obiegDokumentow.ts` — wystawiają TE SAME
// obiekty pod dotychczasowymi nazwami, czyli jeden obiekt pod dwiema nazwami
// importu, nie kopia) i okno agenta Receptora (Zadania, Przepływ, Połączenia).
// Bez tego okno byłoby trzecią kopią tych samych zdań — i pierwszą, której
// żadna bramka nie zestawiałaby z panelem.
//
// TRZY RZECZY, KTÓRE TEN PLIK PILNUJE:
//
//  * NIE IMPORTUJE NICZEGO i jest wystawiony SUBŚCIEŻKĄ
//    `@insolitum/types/receptor`, bez sufiksów `.js`. Indeks pakietu
//    re-eksportuje moduły z `.js` (NodeNext), a tego nie rozwiązuje webpack
//    Next.js (CLAUDE.md, „tsc --noEmit NIE jest dowodem…”). Powłoka i okno
//    agenta są aplikacjami Vite; subścieżka nie ciągnie za sobą indeksu.
//  * USŁUGA GO NIE IMPORTUJE. `receptor-core` nie zależy od `@insolitum/types`,
//    a jego obraz kopiuje wyłącznie `packages/device-auth` (wariant A planu,
//    S21) — więc zbiory stanów żyją tu i w usłudze w dwóch kopiach. Zgodność
//    z usługą, z agentem i z CHECK-ami migracji pilnuje
//    `tests/unit/apps/rodzajeZadanReceptora.test.ts`; kopia bez bramki
//    rozjechałaby się po cichu.
//  * „KLUCZ DOSTĘPU”, NIE „TOKEN”, I „STRAŻNIK FIRMY” w każdym zdaniu dla
//    człowieka (D18, D12). Kody (`odmowa_tokenu`, `brak_tokenu`) zostają — to
//    kontrakt z bazą (CHECK 785), nie tekst. Pilnuje tego ta sama bramka.
//
// Słownika zdań dla KODÓW WYNIKÓW zadań tu nie ma (D8). Kody
// (`KODY_BLEDU_DOKUMENTOW` i rodzeństwo) nie mają zdań w żadnym miejscu
// systemu, więc słownik byłby treścią bez źródła i bez drugiego czytelnika.
// Okno pokazuje kod w mono z „szczegóły w dzienniku”.
// =============================================================================

// ── Rodzaje zadań agenta ────────────────────────────────────────────────────

export interface OpisRodzajuZadania {
  etykieta: string;
  opis: string;
  /**
   * Czy panel może ten rodzaj ZLECIĆ. `false` = rodzaj zleca wyłącznie tor
   * serwisowy (zlecenie produkcyjne z Mandatum); panel go tylko POKAZUJE
   * w kolejce. Zbiór `true` musi równać się `RODZAJE_ZADAN_Z_PANELU` usługi.
   */
  zPanelu: boolean;
}

/** Rodzaj, którym agent czyta magazyny i zmiany do nastaw zlecenia. */
export const RODZAJ_ODCZYTU_NASTAW = 'receptor.odczyt.nastawy';

/**
 * Rodzaj, którym agent czyta listę osób z `dbo.[User]` — BEZ PIN-ów, tylko ich
 * stan (`zgodny`/`niezgodny`/`brak`). Kontrakt z migracją 758 i agentem 0.5.0.
 */
export const RODZAJ_ODCZYTU_UZYTKOWNIKOW = 'receptor.odczyt.uzytkownicy';

/**
 * Rodzaj, którym agent czyta PIN JEDNEJ osoby. Zleca go wyłącznie trasa
 * pracownika w receptor-core (nie lista zadań), a PIN jedzie od razu do
 * `receptor_pin_przypisz` — `wynik` niesie tylko `{status, id_user, worker_id}`.
 */
export const RODZAJ_POBRANIA_PINU = 'receptor.pin.pobierz';

/**
 * Rodzaje obiegu dokumentów (UD-11a, migracje 786/787). Zleca je WYŁĄCZNIE
 * harmonogram usługi (`zrodlo_system = 'harmonogram'`), gdy ktoś włączył obieg —
 * człowiek nie ma przycisku, który zlecałby je z panelu, bo ręczny odczyt
 * strony przesunąłby kursor poza harmonogramem i poza oknem 5 min.
 */
export const RODZAJ_ODCZYTU_DOKUMENTOW = 'receptor.odczyt.dokumenty';
export const RODZAJ_POTWIERDZENIA_DOKUMENTOW = 'receptor.dokumenty.potwierdz';

/**
 * WSZYSTKIE rodzaje zadań — klucze = kontrakt z usługą (`RODZAJE_ZADAN`),
 * z agentem (`RODZAJE_OBSLUGIWANE`) i z więzem `receptor_agent_tasks_rodzaj_chk`.
 * Etykieta służy liście „Ostatnie zadania” w panelu i działowi Zadania w oknie
 * agenta, `zPanelu` — przyciskom panelu.
 */
export const RODZAJE_ZADAN_RECEPTORA: Record<string, OpisRodzajuZadania> = {
  'receptor.ping': {
    etykieta: 'Test łączności',
    opis: 'Agent odsyła wersję, nazwę hosta i to, które poświadczenia ma — dowód, że pętla zadań chodzi.',
    zPanelu: true,
  },
  'receptor.odczyt.kartoteka': {
    etykieta: 'Odczyt kartoteki',
    opis: 'Agent czyta z bazy Receptora warianty produktu z identyfikatorem receptury czynnej.',
    zPanelu: true,
  },
  [RODZAJ_ODCZYTU_NASTAW]: {
    etykieta: 'Odczyt magazynów i zmian',
    opis: 'Agent czyta z bazy Receptora magazyny i zmiany — do list wyboru w nastawach zlecenia.',
    zPanelu: true,
  },
  'receptor.zlecenie.utworz': {
    etykieta: 'Zlecenie produkcyjne',
    opis: 'Agent tworzy w Receptorze niepotwierdzone zlecenie produkcyjne wysłane z Mandatum.',
    zPanelu: false,
  },
  [RODZAJ_ODCZYTU_UZYTKOWNIKOW]: {
    etykieta: 'Odczyt osób',
    opis: 'Agent czyta z bazy Receptora listę osób (bez PIN-ów) — do powiązania z pracownikami.',
    zPanelu: true,
  },
  [RODZAJ_POBRANIA_PINU]: {
    etykieta: 'Pobranie PIN-u',
    opis: 'Agent czyta PIN jednej osoby z Receptora i przekazuje go wprost do kartoteki pracownika — PIN nie zostaje w wyniku zadania.',
    zPanelu: false,
  },
  [RODZAJ_ODCZYTU_DOKUMENTOW]: {
    etykieta: 'Odczyt dokumentów',
    opis: 'Agent czyta z Receptora kolejną stronę dokumentów magazynowych. Zleca je sam harmonogram, gdy obieg dokumentów jest włączony — nie ma na to przycisku.',
    zPanelu: false,
  },
  [RODZAJ_POTWIERDZENIA_DOKUMENTOW]: {
    etykieta: 'Potwierdzenie dokumentów',
    opis: 'Agent potwierdza w Receptorze odebrane dokumenty i sprawdza, czy nie zmieniły się u wystawcy. Zleca je sam harmonogram, gdy obieg dokumentów jest włączony — nie ma na to przycisku.',
    zPanelu: false,
  },
  'receptor.karta.utworz': {
    etykieta: 'Zakładanie karty w Receptorze',
    opis: 'Agent zakłada w Receptorze kartę wyrobu z zasiewu kartoteki (jednorazowo, przy właścicielu).',
    zPanelu: false,
  },
  'receptor.zlecenie.stan': {
    etykieta: 'Odczyt stanu zleceń',
    opis: 'Agent czyta z bazy Receptora stan i postęp zleceń wysłanych z Mandatum (zatwierdzenie, produkcja, partie, zakończenie). Zleca harmonogram usługi.',
    zPanelu: false,
  },
  'receptor.krok.wynik': {
    etykieta: 'Wynik kroku z Temperatio',
    opis: 'Agent zapisuje w Receptorze wynik kroku „Przygotowanie wody” albo „Temperatura ciasta” zmierzony w Temperatio. Zleca go sam pomiar na telefonie, gdy partia doszła do tego kroku — nie ma na to przycisku.',
    zPanelu: false,
  },
};

// ── Wersja Receptora: stan modułu produkcji (agent 0.5.2, migracja 760) ─────

/** `GetStatus.ModuleState` Receptora (i jego `nie_podaje`/`blad`) → zdanie dla człowieka. */
export const OPIS_STANU_PRODUKCJI: Record<string, string> = {
  Starting: 'uruchamia się',
  WarmingUp: 'rozgrzewa się',
  Ready: 'gotowa',
  Faulted: 'błąd uruchamiania (Receptor ponawia sam)',
  Blocked: 'zablokowana',
  nie_podaje: 'nie podaje (moduł produkcji bez konfiguracji)',
  blad: 'stanu nie odczytano',
};

// ── Zdolności integracji: powody „nie sprawdzono” (UD-4, migracja 785) ──────

/**
 * Powody `nie_sprawdzono` sondy tras Receptora — CHECK
 * `receptor_agents_receptor_zdolnosci_powod_chk` (785). Kopie żyją w
 * receptor-core (`zdolnosci.ts`) i w agencie (`main/zdolnosci.ts`).
 */
export const POWODY_ZDOLNOSCI = [
  'konfiguracja_niepotwierdzona',
  'brak_api',
  'brak_tokenu',
  'api_obce',
  'odmowa_tokenu',
  'blad_polaczenia',
  'nieznana_odpowiedz',
] as const;
export type PowodZdolnosci = (typeof POWODY_ZDOLNOSCI)[number];

/** Kompletność (każdy powód ma zdanie) i brak nadmiaru sprawdza kompilator. */
const OPISY_POWODOW_ZDOLNOSCI = {
  konfiguracja_niepotwierdzona: 'konfiguracja agenta czeka na potwierdzenie',
  brak_api: 'agent nie ma adresu API Receptora',
  brak_tokenu: 'integracja z Receptorem nie jest zapisana w agencie',
  api_obce: 'API Receptora należy do innej bazy niż wskazana (strażnik firmy)',
  odmowa_tokenu: 'Receptor odrzucił klucz dostępu integracji — zapisz integrację ponownie',
  blad_polaczenia: 'API Receptora nie odpowiada',
  nieznana_odpowiedz: 'Receptor odpowiedział w nieznany sposób',
} satisfies Record<PowodZdolnosci, string>;

/**
 * Kod powodu „nie sprawdzono” (CHECK 785) → zdanie dla człowieka. Indeks
 * napisem, bo kod przychodzi z bazy albo z pulsu i bywa spoza zbioru (nowsza
 * migracja) — czytelnik daje wtedy własne zdanie nazwane, nie pustkę.
 */
export const OPIS_POWODU_ZDOLNOSCI: Record<string, string> = OPISY_POWODOW_ZDOLNOSCI;

// ── Kursor obiegu dokumentów (UD-11a, migracja 786, plan 53 §13.6.5) ────────

/**
 * Stany kursora — KOPIA `STANY_KURSORA` z receptor-core i CHECK-a
 * `receptor_kursory_stan_chk` (786). Bramka zestawia wszystkie trzy.
 */
export const STANY_KURSORA_RECEPTORA = [
  'aktywny',
  'wstrzymany',
  'kontrakt_nieznany',
  'inna_instalacja',
  'epoka_zmieniona',
  'max_seq_ponizej_kursora',
  'max_seq_sprzeczny',
  'dokument_niepoprawny',
  'dokument_za_duzy',
  'instalacja_w_innej_organizacji',
] as const;

export type StanKursoraReceptora = (typeof STANY_KURSORA_RECEPTORA)[number];

/**
 * Jak obieg wychodzi ze stanu (tabela §13.6.5):
 *  * `dziala` — nie wychodzi, bo nie stoi;
 *  * `wznow` — owner, admin albo super-admin przyciskiem **Wznów** (z powodem);
 *  * `automat` — wznowi się sam po weryfikacji, człowiek nie ma tu przycisku
 *    (funkcja stanu odmawia kodem `stan_automatyczny`);
 *  * `przepiecie` — wyłącznie Insolitum, przyciskiem **Przepnij**.
 */
export type SposobWyjsciaZeStanu = 'dziala' | 'wznow' | 'automat' | 'przepiecie';

export const WYJSCIE_ZE_STANU: Record<StanKursoraReceptora, SposobWyjsciaZeStanu> = {
  aktywny: 'dziala',
  wstrzymany: 'wznow',
  kontrakt_nieznany: 'wznow',
  dokument_niepoprawny: 'wznow',
  dokument_za_duzy: 'wznow',
  max_seq_ponizej_kursora: 'automat',
  max_seq_sprzeczny: 'przepiecie',
  inna_instalacja: 'przepiecie',
  epoka_zmieniona: 'przepiecie',
  instalacja_w_innej_organizacji: 'przepiecie',
};

/** Zdanie „co dalej” dla stanów wymagających przepięcia — jedno, żeby nie rozjechały się cztery. */
export const ZDANIE_WYMAGA_PRZEPIECIA = 'Wymaga przepięcia przez Insolitum.';
/** Zdanie dla stanu, z którego obieg wychodzi sam. */
export const ZDANIE_AUTOMATYCZNA_WERYFIKACJA =
  'Trwa automatyczna weryfikacja — obieg wznowi się sam.';

/**
 * Jedno zdanie na stan, po polsku, dla właściciela firmy. Stany wymagające
 * przepięcia kończą się {@link ZDANIE_WYMAGA_PRZEPIECIA}, żeby człowiek wiedział,
 * że nie ma tu nic do kliknięcia.
 */
export const ZDANIA_STANOW: Record<StanKursoraReceptora, string> = {
  aktywny: 'Obieg działa — dokumenty z Receptora są odczytywane na bieżąco.',
  wstrzymany:
    'Obieg wstrzymany — dokumenty z Receptora nie są odczytywane. Wznów go, gdy powód ustąpi.',
  kontrakt_nieznany:
    'Receptor odpowiedział w formacie dokumentów, którego Universe nie zna — odczyt zatrzymany. Po naprawie po stronie Receptora albo agenta wznów obieg.',
  inna_instalacja: `Agent czyta teraz inną bazę Receptora niż ta, z którą obieg został związany — odczyt zatrzymany. ${ZDANIE_WYMAGA_PRZEPIECIA}`,
  epoka_zmieniona: `Baza Receptora została odtworzona albo zastąpiona inną (zmieniła się numeracja dokumentów) — odczyt zatrzymany. ${ZDANIE_WYMAGA_PRZEPIECIA}`,
  max_seq_ponizej_kursora: `Najwyższy numer dokumentu w Receptorze jest niższy niż pozycja odczytu. ${ZDANIE_AUTOMATYCZNA_WERYFIKACJA}`,
  max_seq_sprzeczny: `Sprawdzenie numeracji dokumentów w Receptorze dało sprzeczny wynik — odczyt zatrzymany. ${ZDANIE_WYMAGA_PRZEPIECIA}`,
  dokument_niepoprawny:
    'Receptor wydał dokument, którego Universe nie umie zarejestrować — odczyt zatrzymany. Po naprawie po stronie Receptora albo agenta wznów obieg.',
  dokument_za_duzy:
    'Receptor wydał dokument większy, niż Universe przyjmuje — odczyt zatrzymany. Po naprawie po stronie Receptora albo agenta wznów obieg.',
  instalacja_w_innej_organizacji: `Ta baza Receptora jest już związana z obiegiem innej organizacji — odczyt zatrzymany. ${ZDANIE_WYMAGA_PRZEPIECIA}`,
};

/**
 * Powody niezlecania — KOPIA `POWODY_HARMONOGRAMU` z receptor-core i CHECK-a
 * `receptor_kursory_harmonogram_powod_chk` (786).
 */
export const POWODY_HARMONOGRAMU_RECEPTORA = [
  'agent_bez_zdolnosci',
  'trasa_niepotwierdzona',
  'straznik',
  'licencja',
  'kolejka_zablokowana',
  'agent_nie_prowadzi',
  'rodzaj_niedozwolony_w_bazie',
] as const;

export type PowodHarmonogramuReceptora = (typeof POWODY_HARMONOGRAMU_RECEPTORA)[number];

/**
 * Zdanie na powód — mówi, CO ZROBIĆ, a nie tylko, co się stało. Kolejność jak
 * w harmonogramie usługi (§13.6.4 p. 1): pierwszy niespełniony warunek jest
 * powodem, więc człowiek naprawia je po kolei.
 */
export const ZDANIA_POWODOW_HARMONOGRAMU: Record<PowodHarmonogramuReceptora, string> = {
  agent_bez_zdolnosci:
    'Agent nie zgłasza obsługi dokumentów — zaktualizuj agenta na serwerze z Receptorem.',
  trasa_niepotwierdzona:
    'Receptor na tym serwerze nie potwierdził trasy dokumentów — sprawdź jego wersję (wymagana 3.0.10.0 lub nowsza) w wierszu synchronizacji dokumentów.',
  straznik:
    'Agent nie potwierdził, że baza i API Receptora należą do tej firmy (porównanie NIP-u) — sprawdź konfigurację agenta.',
  licencja: 'Licencja agenta nie obejmuje tego serwera albo wygasła — skontaktuj się z opiekunem.',
  kolejka_zablokowana:
    'Nie udało się uporządkować kolejki zadań agenta po zmianie jego bazy — usługa ponawia to przy każdym pulsie; jeśli stan się utrzymuje, zgłoś to opiekunowi.',
  agent_nie_prowadzi:
    'Obiegu nie prowadzi żaden działający agent (poprzedni został odwołany albo usunięty) — włącz obieg przy agencie, który ma go obsługiwać.',
  rodzaj_niedozwolony_w_bazie:
    'Baza Universe nie zna jeszcze nowych rodzajów zadań dokumentowych (trwa wdrożenie) — usługa ponowi próbę w następnym oknie; jeśli stan się utrzymuje, zgłoś to opiekunowi.',
};

// ── Gotowość zleceń z Mandatum (`integracja-receptora/1`, UD-6a) ────────────

/**
 * Gotowość — KOPIA `STANY_GOTOWOSCI` z receptor-core (`integracja.ts`).
 * Kolejność jest kolejnością rozstrzygania w usłudze.
 */
export const STANY_GOTOWOSCI_RECEPTORA = [
  'gotowy',
  'brak_agenta',
  'agent_milczy',
  'brak_zdolnosci',
  'brak_nastaw',
] as const;

export type StanGotowosciReceptora = (typeof STANY_GOTOWOSCI_RECEPTORA)[number];

// ── Kartoteka z Receptora (UD-22, migracja 804) ─────────────────────────────

/**
 * Stany źródła kartoteki — KOPIA CHECK-a `kartoteka_zrodla_stan_chk` (804).
 * Zdań tu nie ma: zdanie dla stanu i dla ostatniego kodu porażki daje usługa
 * (`receptor_kartoteka_zdanie`, 812), ta sama, którą czyta panel.
 */
export const STANY_ZRODLA_KARTOTEKI = [
  'nieaktywne',
  'aktywne',
  'inna_instalacja',
  'epoka_zmieniona',
  'instalacja_nie_podaje',
] as const;

export type StanZrodlaKartoteki = (typeof STANY_ZRODLA_KARTOTEKI)[number];

// ── Odczyt przepływu dla okna agenta (`przeplyw-agenta/1`, D7, §11.5) ───────
//
// Odpowiedź `GET /api/receptor/agents/przeplyw` (receptor-core, za bramą
// maszyny). Wzorzec odpowiedzi: `tests/fixtures/przeplyw-agenta/v1/odpowiedz.json`.
//
// Pola niosące STANY z Universe (`gotowosc`, `kursor`, `zrodlo`, powody) są
// typowane napisem, a nie unią zbioru: usługa wdraża się osobno od agenta, więc
// stan dopisany nowszą migracją przychodzi do starszego okna — i okno ma go
// nazwać („stan … — nowszy niż to okno”), a nie udawać, że go nie ma. Zbiory
// znane w chwili wydania są wyżej i niżej (`STANY_*`, `POWODY_NIE_DOTYCZY_*`).

export const KONTRAKT_PRZEPLYWU = 'przeplyw-agenta/1';

/** Zamknięta lista sekcji odpowiedzi — kolejność to kolejność na mapie (od Universe na zewnątrz). */
export const SEKCJE_PRZEPLYWU = [
  'organizacja',
  'zlecenia',
  'dokumenty',
  'kartoteka',
  'mandatum',
  'system_ksiegowy',
  'kroki',
] as const;

export type SekcjaPrzeplywu = (typeof SEKCJE_PRZEPLYWU)[number];

/** Organizacja, dla której pracuje agent — wyłącznie z kontekstu bramy maszyny. */
export interface DaneOrganizacjiPrzeplywu {
  nazwa: string;
}

/** Gotowość zleceń z Mandatum — tymi samymi wywołaniami, co `/service/integracja`. */
export interface DaneZlecenPrzeplywu {
  /** Stan ze zbioru {@link STANY_GOTOWOSCI_RECEPTORA}. */
  gotowosc: string;
  /** Kod maszynowy powodu (np. `nastawy_missing`) albo `null` przy `gotowy`. */
  powod: string | null;
  /** To samo zdanie, które widzi Mandatum, albo `null` przy `gotowy`. */
  komunikat: string | null;
  /** Czy zlecenia trafiają do TEGO agenta (`gotowy` dla innego serwera to „nie dotyczy”). */
  ten_agent: boolean;
}

/** Kursor obiegu dokumentów prowadzony przez tego agenta. */
export interface DaneDokumentowPrzeplywu {
  /** Stan ze zbioru {@link STANY_KURSORA_RECEPTORA}. */
  kursor: string;
  porazki_z_rzedu: number;
  ostatni_sukces_at: string | null;
  /** Powód ze zbioru {@link POWODY_HARMONOGRAMU_RECEPTORA} albo `null` (harmonogram zleca). */
  powod_harmonogramu: string | null;
}

/** Źródło kartoteki prowadzone przez tego agenta. */
export interface DaneKartotekiPrzeplywu {
  /**
   * Stan ze zbioru {@link STANY_ZRODLA_KARTOTEKI}. Pole NIE nazywa się `stan`,
   * choć tak nazywa się kolumna: `stan` rozróżnia sekcje (`ok`/`nie_dotyczy`/
   * `blad_odczytu`), a `'ok' & 'aktywne'` to typ pusty.
   */
  zrodlo: string;
  ostatni_spis_at: string | null;
  porazki_z_rzedu: number;
  /** Zdanie usługi dla stanu albo ostatniego kodu porażki (812) — `null`, gdy nie ma czego mówić. */
  zdanie: string | null;
}

/** Most dokumentów do Mandatum — deklaracja odbiorcy (809) i ostatnie zgłoszenie. */
export interface DaneMandatumPrzeplywu {
  /** `null` = Mandatum nie zadeklarowało odbioru (stan nazwany, nie brak danych). */
  deklaracja: { rodzaje: string[] } | null;
  ostatnie_zgloszenie_at: string | null;
  /** Kontaktu Mandatum z Universe nikt nie mierzy — stała kontraktu, nigdy zieleń. */
  kontakt: 'nie_mierzony';
}

/** Dokumenty u odbiorcy w systemie księgowym — liczniki, bez treści dokumentów. */
export interface DaneSystemuKsiegowegoPrzeplywu {
  /** Nazwa systemu z deklaracji odbiorcy — nigdy z literału; `null`, gdy deklaracji nie ma. */
  system: string | null;
  do_decyzji: number;
  wstrzymane: number;
  /** Połączenia Mandatum z systemem księgowym Universe nie zna — stała kontraktu. */
  polaczenie: 'nie_podaje';
}

/**
 * Kroki zewnętrzne partii Receptora prowadzone przez TEGO agenta (`kroki-zewn/1`, E2–E4,
 * agent 0.7.2) — same liczniki i chwile, bez treści kroku i wyniku (masy, temperatury,
 * numery zleceń, nazwiska).
 */
export interface DaneKrokowPrzeplywu {
  /** Kroki `Announced`/`Current`, które nie zniknęły z Receptora. */
  w_toku: number;
  /**
   * Partie w kolejce „Czeka na Ciebie” (ta sama definicja co 831): z krokiem w toku i bez
   * żywej partii Temperatio. Liczone partie, nie kroki.
   */
  czeka_na_temperatio: number;
  /** Kroki w toku z wynikiem zapisanym w Universe, którego Receptor jeszcze nie przyjął. */
  wyniki_w_drodze: number;
  /** Zapis najstarszego wyniku w drodze; `null`, gdy nic nie jest w drodze. */
  najstarszy_wynik_w_drodze_at: string | null;
  /** Ostatni wynik oddany przez Receptor jako krok `Done` z tym samym pomiarem. */
  ostatni_wynik_przyjety_at: string | null;
  /** Ostatnia zmiana kroku przyjęta z pulsu tego agenta. */
  ostatnia_zmiana_at: string | null;
}

export interface DanePrzeplywu {
  organizacja: DaneOrganizacjiPrzeplywu;
  zlecenia: DaneZlecenPrzeplywu;
  dokumenty: DaneDokumentowPrzeplywu;
  kartoteka: DaneKartotekiPrzeplywu;
  mandatum: DaneMandatumPrzeplywu;
  system_ksiegowy: DaneSystemuKsiegowegoPrzeplywu;
  kroki: DaneKrokowPrzeplywu;
}

/**
 * Sekcja odpowiedzi. Awaria jednej sekcji nie kładzie całej odpowiedzi: usługa
 * oddaje 200 z `blad_odczytu` w tej jednej, a okno mówi o niej „Universe tego
 * nie podaje” — nigdy zieleń.
 */
export type SekcjaOdczytu<T> =
  | ({ stan: 'ok' } & T)
  | { stan: 'nie_dotyczy'; powod: string }
  | { stan: 'blad_odczytu' };

export type PrzeplywAgenta = {
  kontrakt: typeof KONTRAKT_PRZEPLYWU;
  /**
   * Chwila, w której Universe policzyło odpowiedź (usługa trzyma ją 30 s). Nazwa
   * jak w pozostałych odpowiedziach receptor-core (`integracja-receptora/1`,
   * kartoteka i dokumenty Mandatum) — bez `_at`.
   */
  odczytano: string;
} & { [S in SekcjaPrzeplywu]: SekcjaOdczytu<DanePrzeplywu[S]> };

/**
 * Pola sekcji `ok` — ZAMKNIĘTA lista: nic spoza niej nie wyjeżdża z Universe do
 * okna (bez NIP-ów, kontrahentów, numerów, cen, nazwisk, PIN-ów i tekstów
 * z obcych systemów). Kompilator pilnuje obu stron: `Record` wymaga każdego pola
 * typu, a literał obiektu odrzuca pole spoza typu. Wartość `true` nie niesie
 * znaczenia — czytelnicy biorą `Object.keys`.
 */
export const POLA_SEKCJI_PRZEPLYWU: {
  readonly [S in SekcjaPrzeplywu]: Readonly<Record<keyof DanePrzeplywu[S], true>>;
} = {
  organizacja: { nazwa: true },
  zlecenia: { gotowosc: true, powod: true, komunikat: true, ten_agent: true },
  dokumenty: {
    kursor: true,
    porazki_z_rzedu: true,
    ostatni_sukces_at: true,
    powod_harmonogramu: true,
  },
  kartoteka: { zrodlo: true, ostatni_spis_at: true, porazki_z_rzedu: true, zdanie: true },
  mandatum: { deklaracja: true, ostatnie_zgloszenie_at: true, kontakt: true },
  system_ksiegowy: { system: true, do_decyzji: true, wstrzymane: true, polaczenie: true },
  kroki: {
    w_toku: true,
    czeka_na_temperatio: true,
    wyniki_w_drodze: true,
    najstarszy_wynik_w_drodze_at: true,
    ostatni_wynik_przyjety_at: true,
    ostatnia_zmiana_at: true,
  },
};

/**
 * Powody `nie_dotyczy` znane w chwili wydania (§7.2, §11.5):
 *  * organizacja — żadnego: agent zawsze pracuje dla jakiejś;
 *  * `zlecenia_niewlaczone` — żaden agent organizacji nie ma nastaw zleceń
 *    (`brak_nastaw` z `nastawy_missing`), więc to stan nazwany, nie czerwień;
 *  * `obieg_niewlaczony` — organizacja nie ma kursora obiegu dokumentów; most do
 *    Mandatum i liczniki systemu księgowego stoją na tym kursorze (809, B-1);
 *  * `zrodlo_nie_zalozone` — kartoteka nie jest prowadzona z Receptora;
 *  * `prowadzi_inny_serwer` — kursor albo źródło prowadzi inny agent tej
 *    organizacji, więc usługa nie oddaje temu oknu jego liczników;
 *  * `kroki_niewlaczone` — organizacja nie ma kroków zewnętrznych włączonych
 *    (brak wiersza `receptor_kroki_zewn_organizacje`, 829; włącza etap E6).
 */
export const POWODY_NIE_DOTYCZY_PRZEPLYWU: {
  readonly [S in SekcjaPrzeplywu]: readonly string[];
} = {
  organizacja: [],
  zlecenia: ['zlecenia_niewlaczone'],
  dokumenty: ['obieg_niewlaczony', 'prowadzi_inny_serwer'],
  kartoteka: ['zrodlo_nie_zalozone', 'prowadzi_inny_serwer'],
  mandatum: ['obieg_niewlaczony', 'prowadzi_inny_serwer'],
  system_ksiegowy: ['obieg_niewlaczony', 'prowadzi_inny_serwer'],
  kroki: ['kroki_niewlaczone'],
};

// ── Puls agenta co 5 s (plan 59, decyzja właściciela z 25.10.2026) ──────────

/**
 * Rodzaje zadań „pilnego pasa” (D4, plan 59 K1/K2/S5). Zajęty agent (porcja w toku,
 * `max_tasks: 0`) może poprosić o najwyżej jedno zadanie z tej listy (`max_pilnych: 1`),
 * a przy `max_tasks > 0` usługa wydaje je PRZED zadaniami starszymi (FIFO zostaje dla
 * reszty). Dziś jedyny pilny rodzaj to wynik kroku z Temperatio: piekarz stoi przy
 * terminalu Receptora i czeka na niego w trakcie partii.
 *
 * KOPIA w `services/receptor-core/src/pietroPulsu.ts` (usługa nie importuje tego
 * pakietu — wariant A, S21). Zgodność kopii pilnuje
 * `tests/unit/apps/kontraktPulsuAgenta.test.ts`.
 */
export const RODZAJE_PILNE_RECEPTORA = ['receptor.krok.wynik'] as const;

/**
 * Granice okresu pulsu zgłaszanego przez agenta (`puls_okres_s`) i hamulca
 * serwera (`puls_min_okres_s`), w sekundach. Te same liczby co CHECK
 * `receptor_agents_puls_okres_chk` migracji 840 — zgodność pliku migracji ze
 * stałymi usługi pilnuje `services/receptor-core/src/__tests__/pulsOkresWiez.test.ts`,
 * a kopii w usłudze z tymi tutaj — `tests/unit/apps/kontraktPulsuAgenta.test.ts`.
 * Sufit 300 s (nie 3 600) zamyka maskowanie ciszy: próg „offline” w panelu to
 * `max(90 s, 3 × okres)`, czyli najwyżej 15 min (P12).
 */
export const PULS_OKRES_MIN_S = 5;
export const PULS_OKRES_MAX_S = 300;
/**
 * Okres agenta, który go nie podaje (0.7.x albo serwer bez 840): 60 s, czyli
 * dzisiejszy rytm. Panel liczy z niego próg „offline” 3 min (K7).
 */
export const PULS_OKRES_NIEZNANY_S = 60;
